import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  Output,
  ViewChild,
} from "@angular/core";
import { Cesium3DTileset, KmlDataSource, Viewer, Cartesian3 } from "cesium";
import { Building, MapImageOverlay } from "../../../../core/models";
import { BuildingService, ScoutPointPhotoService } from "../../../../core/services";
import { RestClient } from "../../../../core/services/rest-client";
import { Observable, firstValueFrom, race } from "rxjs";
import { saveAs } from "file-saver";
import { KmlBuilder, KmlBuilderPath, formatFloor } from "../kml-builder";

@Component({
  selector: "fes-kml-viewer",
  templateUrl: "./kml-viewer.component.html",
  styleUrls: ["./kml-viewer.component.scss"],
})
export class KmlViewerComponent implements AfterViewInit, OnDestroy {
  @Input() paths: KmlBuilderPath[] = [];
  @Input("pathIds") selectedPathIds: string[] = [];
  @Input() mainFolderName?: string;
  @Input() bAnchorToGround = false;
  @Input() bIncludeBuildings = true;
  @Input() bSatelliteTerrain = false;
  @Input() overlaysByPathId: Record<string, MapImageOverlay | undefined> = {};
  @Output() close = new EventEmitter<void>();
  @ViewChild("cesiumMapDiv") public cesiumMapDiv: ElementRef;

  buildingClient: RestClient<Building>;
  kmlDataSources: KmlDataSource[] = [];
  pathIdsSelected: { [id: string]: boolean } = {};
  bProcessing = false;
  bLoading = true;
  viewer: Viewer | null;

  osmBuildingsTileset: Cesium3DTileset | null;
  worldTerrain = Cesium.Terrain.fromWorldTerrain();
  bLoadingBuildingTiles = false;
  private readonly surfaceOffsetMeters = 0.25; // render HA discs slightly below HA altitude so points draw on top
  private _debugUnhandledRegistered = false;
  private additionalEntities: any[] = []; // Track vertical lines, discs, and other entities added per path
  private overlayImageCache = new Map<string, string>();
  bShowOverlays = true;

  constructor(
    protected ngZone: NgZone,
    public buildingService: BuildingService,
    protected photoService: ScoutPointPhotoService
  ) {}

  ngAfterViewInit(): void {
    this.setupUnhandledPromiseLogging();
    this.init();
  }

  ngOnDestroy(): void {
    this.viewer?.destroy();
  }

  async init() {
    try {
      this.bProcessing = true;
      if (!this.selectedPathIds?.length) {
        this.paths?.forEach((path) => (this.pathIdsSelected[path.id] = true));
      } else {
        this.selectedPathIds?.forEach(
          (id) => (this.pathIdsSelected[id] = true)
        );
      }
    } catch (error: any) {
      console.error(error);
    } finally {
      this.bProcessing = false;
      this.bLoading = false;
    }
    this.initCesiumKml();
  }

  async initCesiumKml(bTerrainChanged = false) {
    await this.ngZone.runOutsideAngular(async () => {
    try {
      if (this.viewer && bTerrainChanged) {
        this.osmBuildingsTileset = null;
        this.viewer.destroy();
        this.viewer = null;
      }
      if (!this.viewer) {
        let baseLayer: any = undefined;
        if (!this.bSatelliteTerrain) {
          try {
            const provider: any = new Cesium.UrlTemplateImageryProvider({
              url: "https://cartodb-basemaps-a.global.ssl.fastly.net/light_all/{z}/{x}/{y}.png",
            });
            // Log imagery provider errors if any
            if (provider?.errorEvent?.addEventListener) {
              provider.errorEvent.addEventListener((err: any) =>
                console.error('ImageryProvider errorEvent', err)
              );
            }
            baseLayer = new Cesium.ImageryLayer(provider);
          } catch (e) {
            console.warn('Failed to create base imagery layer', e);
          }
        }
        try {
          this.viewer = new Cesium.Viewer(this.cesiumMapDiv.nativeElement, {
            baseLayer,
            terrain: this.bAnchorToGround ? undefined : this.worldTerrain,
          });
        } catch (e) {
          console.error('Cesium.Viewer creation failed', e);
          throw e;
        }
        try {
          // Terrain provider diagnostics
          const tp: any = this.viewer.terrainProvider as any;
          if (tp?.readyPromise?.then) {
            tp.readyPromise.then(() => console.log('Terrain readyPromise resolved'))
              .catch((err: any) => console.error('Terrain readyPromise rejected', err));
          }
          if (tp?.errorEvent?.addEventListener) {
            tp.errorEvent.addEventListener((err: any) => console.error('Terrain errorEvent', err));
          }
          if ((this.viewer.scene.globe as any)?.tileLoadProgressEvent?.addEventListener) {
            (this.viewer.scene.globe as any).tileLoadProgressEvent.addEventListener((pending: number) => {
              // Useful to see if terrain/imagery stalls
              // console.log('Globe tileLoadProgressEvent pending:', pending);
            });
          }
          // Render error diagnostics
          if (this.viewer.scene?.renderError?.addEventListener) {
            this.viewer.scene.renderError.addEventListener((err: any) => {
              console.error('Scene renderError', err);
            });
          }
        } catch {}
      }
      this.bProcessing = true;
      const viewer = this.viewer;
      this.kmlDataSources.forEach((ds) => viewer!.dataSources.remove(ds));
      this.kmlDataSources = [];
      
      // Clear additional entities (vertical lines, discs, etc.)
      this.additionalEntities.forEach((entity) => {
        try {
          viewer!.entities.remove(entity);
        } catch (e) {
          // Entity might already be removed
        }
      });
      this.additionalEntities = [];
      const selectedPaths = this.pathsFromIds(
        Object.keys(this.pathIdsSelected).filter(
          (id) => this.pathIdsSelected[id]
        )
      );
      selectedPaths.sort((a, b) => a?.pathNumber - b?.pathNumber);
      const strKml = await this.pathsAsKml({
        paths: selectedPaths,
        mainFolderName: this.mainFolderName,
        bAnchorToGround: this.bAnchorToGround,
      });
      const testBlob = new Blob([strKml], {
        type: "application/vnd.google-earth.kml+xml",
      });

      let kmlDs: any;
      try {
        kmlDs = await Cesium.KmlDataSource.load(testBlob);
      } catch (e) {
        console.error('KmlDataSource.load failed', e);
        throw e;
      }
      try {
        if (kmlDs?.errorEvent?.addEventListener) {
          kmlDs.errorEvent.addEventListener((source: any, error: any) => {
            console.error('KML DataSource errorEvent', { source, error });
          });
        }
      } catch {}
      // Fix points positioning when terrain is enabled (flatten is off)
      const ensureVisible = async (entities) => {
        for (const entity of entities) {
          if (entity.point) {
            // Always use NONE to respect the absolute altitude from KML
            entity.point.heightReference = Cesium.HeightReference.NONE;
            entity.point.disableDepthTestDistance = Number.POSITIVE_INFINITY;
            // Ensure points are always visible even when underground
            entity.point.scaleByDistance = undefined;
          }
          if (entity.billboard) {
            entity.billboard.heightReference = Cesium.HeightReference.NONE;
            entity.billboard.disableDepthTestDistance = Number.POSITIVE_INFINITY;
            // Fix billboard scaling to be consistent with ellipses
            entity.billboard.scaleByDistance = undefined;
            entity.billboard.pixelOffset = undefined;
            entity.billboard.alignedAxis = Cesium.Cartesian3.ZERO; // Disable rotation with camera
          }
          if (entity.polyline) {
            entity.polyline.disableDepthTestDistance = Number.POSITIVE_INFINITY;
            if (entity.polyline) {
              if (!this.bAnchorToGround) {
                // When terrain is enabled, don't clamp polylines - let them follow actual heights
                entity.polyline.clampToGround = false;
                entity.polyline.arcType = Cesium.ArcType.NONE;
              } else {
                entity.polyline.clampToGround = false;
                entity.polyline.arcType = Cesium.ArcType.NONE;
              }
              entity.polyline.depthFailMaterial =
                entity.polyline.material ||
                new Cesium.ColorMaterialProperty(Cesium.Color.RED);
            }
          }
          if (entity.polygon) {
            entity.polygon.material =
              entity.polygon.material ||
              Cesium.ColorMaterialProperty.fromColor(
                Cesium.Color.RED.withAlpha(0.5)
              );
            entity.polygon.disableDepthTestDistance = Number.POSITIVE_INFINITY;
            entity.polygon.heightReference = Cesium.HeightReference.NONE;
          }
        }
      }
      await ensureVisible(kmlDs.entities.values);
      let viewerDs: KmlDataSource;
      try {
        viewerDs = (await viewer!.dataSources.add(kmlDs)) as KmlDataSource;
      } catch (e) {
        console.error('viewer.dataSources.add failed', e);
        throw e;
      }
      this.kmlDataSources.push(viewerDs);

      // Zoom to KML first so picks/projected window coords land on-canvas
      try {
        await viewer!.zoomTo(viewerDs);
      } catch (e) {
        console.warn('zoomTo(viewerDs) failed; continuing', e);
      }

      // Initialize building tiles next
      try {
        await this.initBuildingsTileset();
      } catch (e) {
        console.error('initBuildingsTileset failed', e);
      }

      // Now that camera is framed and tiles initializing, create virtual building cylinders
      if (this.bIncludeBuildings && !this.bAnchorToGround) {
        try {
          await this.createVirtualBuildingCylindersFromPaths(selectedPaths, viewer!);
        } catch (e) {
          console.error('createVirtualBuildingCylindersFromPaths failed', e);
        }
      }

      if (selectedPaths.length) {
        try {
          await this.addImageOverlaysToScene(selectedPaths, viewer!);
        } catch (e) {
          console.error('addImageOverlaysToScene failed', e);
        }
      }
    } catch (error: any) {
      console.error(error);
    } finally {
      this.bProcessing = false;
    }
    });
  }

  async initBuildingsTileset() {
    this.bLoadingBuildingTiles = true;
    console.log(`🏢 Initializing building tileset - bAnchorToGround: ${this.bAnchorToGround}, bIncludeBuildings: ${this.bIncludeBuildings}`);
    try {
      if (!this.bAnchorToGround && this.bIncludeBuildings) {
        console.log(`✅ Conditions met for loading OSM building tiles`);
        if (!this.osmBuildingsTileset) {
          console.log(`🔄 Creating OSM buildings tileset...`);
          try {
            this.osmBuildingsTileset = await Cesium.createOsmBuildingsAsync();
          } catch (e) {
            console.error('createOsmBuildingsAsync failed', e);
            throw e;
          }
          console.log(`✅ OSM buildings tileset created successfully`);
          try {
            this.osmBuildingsTileset!.style = new Cesium.Cesium3DTileStyle({
              color: "rgba(255, 255, 255, 0.08)",
            });
          } catch (e) {
            console.warn('Setting OSM style failed', e);
          }
          try {
            console.log(`🌍 Adding OSM buildings tileset to scene...`);
            this.viewer!.scene.primitives.add(this.osmBuildingsTileset);
          } catch (e) {
            console.error('Adding OSM tileset to scene failed', e);
          }
          try {
            const rp: any = (this.osmBuildingsTileset as any).readyPromise;
            if (rp && rp.then) {
              rp.then(() => console.log('OSM tileset readyPromise resolved'))
                .catch((err: any) => console.error('OSM tileset readyPromise rejected', err));
            }
            const anyTs: any = this.osmBuildingsTileset as any;
            if (anyTs?.tileFailed?.addEventListener) {
              anyTs.tileFailed.addEventListener((err: any) => console.error('OSM tileFailed', err));
            }
          } catch {}
          
          // Don't wait for all tiles to load - just wait a reasonable amount of time
          console.log(`⏰ Waiting for initial building tiles to load...`);
          await new Promise(resolve => setTimeout(resolve, 2000)); // 2 second timeout
          console.log(`✅ Building tiles initialization completed (may still be loading in background)`);
        }
      } else {
        console.log(`❌ Conditions not met for OSM building tiles (bAnchorToGround: ${this.bAnchorToGround}, bIncludeBuildings: ${this.bIncludeBuildings})`);
        if (this.osmBuildingsTileset) {
          console.log(`🗑️ Removing existing OSM building tileset`);
          this.viewer!.scene.primitives.remove(this.osmBuildingsTileset);
          this.osmBuildingsTileset = null;
        }
      }
    } catch (e: any) {
      console.error(`❌ Error initializing building tileset:`, e);
    } finally {
      console.log(`🏁 Building tileset initialization finished`);
      this.bLoadingBuildingTiles = false;
    }
  }

  private setupUnhandledPromiseLogging() {
    if (this._debugUnhandledRegistered) return;
    try {
      window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
        try {
          console.error('Unhandled promise rejection', event.reason);
        } catch {}
      });
      this._debugUnhandledRegistered = true;
    } catch {}
  }

  async onPathSelected(pathId: string) {
    this.initCesiumKml();
  }

  async onUpdateMap() {
    this.initCesiumKml();
  }

  pathsFromIds = (pathIds: string[]) =>
    this.paths.filter((p) => pathIds?.includes(p.id));

  get hasOverlays(): boolean {
    return Object.values(this.overlaysByPathId || {}).some((overlay) => !!overlay);
  }

  async pathsAsKml(args: { paths: KmlBuilderPath[]; mainFolderName?: string; bAnchorToGround?: boolean }) {
    let { paths, mainFolderName, bAnchorToGround } = args;
    if (!mainFolderName) {
      const uniqueBuildingIds = [...new Set(paths.map((p) => p.buildingId))];
      if (uniqueBuildingIds.length === 1) {
        const buildingId = uniqueBuildingIds[0];
        const buildingDoc = await this.buildingService.getById(buildingId);
        const commonName = buildingDoc?.commonName;
        mainFolderName =
          buildingId +
          (commonName ? ` - ${this.escapeXMLSpecialChars(commonName)}` : "");
      } else {
        mainFolderName = uniqueBuildingIds.join(", ");
      }
    }
    return new KmlBuilder(paths).buildKml({
      topDocName: null, //TBD - kmz file name?
      mainFolderName,
      verticalOffset: bAnchorToGround ?? this.bAnchorToGround ? undefined : 0.000001,
    });
  }

  onExportAsKmz = async () => {
    this.bProcessing = true;
    try {
      const selectedPaths = this.pathsFromIds(
        Object.keys(this.pathIdsSelected).filter(
          (id) => this.pathIdsSelected[id]
        )
      );
      selectedPaths.sort((a, b) => a?.pathNumber - b?.pathNumber);
      const strKml = await this.pathsAsKml({
        paths: selectedPaths,
        mainFolderName: this.mainFolderName,
        bAnchorToGround: this.bAnchorToGround,
      });
      //'kml'   =>  'application/vnd.google-earth.kml+xml'
      //'kmz'   =>  'application/vnd.google-earth.kmz'
      const blob = new Blob([strKml], {
        type: "application/vnd.google-earth.kml+xml",
      });
      let fileName =
        [...new Set(selectedPaths.map((p) => p.buildingId))].join("_") + ".kml";
      if (selectedPaths.length == 1) {
        const path = selectedPaths[0];
        fileName = `${path.buildingId} P${("" + path.pathNumber).padStart(
          2,
          "0"
        )} - ${formatFloor(path.floor)}.kml`;
      } else if (selectedPaths.length < this.paths.length) {
        const pathsByBuilding = selectedPaths.reduce((acc, path) => {
          if (!acc[path.buildingId]) {
            acc[path.buildingId] = [];
          }
          acc[path.buildingId].push(path);
          return acc;
        }, {} as Record<string, typeof selectedPaths>);
        const buildingSegments = Object.entries(pathsByBuilding).map(
          ([buildingId, paths]) => {
            const pathNumbers = paths.map(
              (p) => "P" + `${p.pathNumber}`.padStart(2, "0")
            );
            return `${buildingId}${
              pathNumbers.length ? "_" + pathNumbers.join("_") : ""
            }`;
          }
        );
        fileName = `${buildingSegments.join("_")}.kml`;
      }
      saveAs(blob, fileName);
    } catch (error: any) {
      console.error(error);
    } finally {
      this.bProcessing = false;
    }
  };

  private async addImageOverlaysToScene(paths: KmlBuilderPath[], viewer: Viewer) {
    if (!paths?.length) {
      return;
    }
    if (!this.bShowOverlays || !this.hasOverlays) {
      return;
    }
    for (const path of paths) {
      try {
        const overlay = this.overlaysByPathId?.[path.id];
        if (!overlay) {
          continue;
        }
        const entity = await this.createOverlayEntity(path, overlay, viewer);
        if (entity) {
          this.additionalEntities.push(entity);
        }
      } catch (error) {
        console.error(`Failed adding overlay for path ${path?.id}`, error);
      }
    }
  }

  onToggleOverlays() {
    this.initCesiumKml();
  }

  private async createOverlayEntity(
    path: KmlBuilderPath,
    overlay: MapImageOverlay,
    viewer: Viewer
  ) {
    const bounds = this.computeOverlayBounds(overlay);
    if (!bounds) {
      console.warn(`Overlay bounds unavailable for path ${path?.id}`);
      return undefined;
    }
    const image = await this.resolveOverlayImage(overlay);
    if (!image) {
      console.warn(`Overlay image unavailable for path ${path?.id}`);
      return undefined;
    }
    const altitude = this.getOverlayAltitude(path, overlay);
    const hasAltitude = altitude != null && isFinite(altitude);

    const rectangleProps: any = {
      coordinates: Cesium.Rectangle.fromDegrees(
        bounds.west,
        bounds.south,
        bounds.east,
        bounds.north
      ),
      material: new Cesium.ImageMaterialProperty({
        image,
        transparent: true,
      }),
      outline: true,
      outlineColor: Cesium.Color.WHITE.withAlpha(0.35),
      classificationType: Cesium.ClassificationType.BOTH,
      heightReference: hasAltitude
        ? Cesium.HeightReference.NONE
        : Cesium.HeightReference.CLAMP_TO_GROUND,
    };
    if (hasAltitude) {
      rectangleProps.height = altitude;
    }

    const entity = viewer.entities.add({
      name: overlay.jpgName || overlay.imageId || `${path.id} overlay`,
      rectangle: rectangleProps,
    });
    return entity;
  }

  private async resolveOverlayImage(overlay: MapImageOverlay) {
    if (!overlay) {
      return undefined;
    }
    if (overlay.imageDataUrl) {
      return overlay.imageDataUrl;
    }
    const imageId = overlay.imageId;
    if (!imageId) {
      return undefined;
    }
    if (this.overlayImageCache.has(imageId)) {
      return this.overlayImageCache.get(imageId);
    }
    if (!this.photoService?.rxCollection) {
      return undefined;
    }
    try {
      const imageDoc = await this.photoService.rxCollection
        .findOne(imageId)
        .exec();
      let dataUrl: string | undefined;
      if (imageDoc) {
        dataUrl = await this.photoService.loadLocalPhotoFile(
          imageDoc._id,
          imageDoc.fileChecksum
        );
        if (!dataUrl) {
          dataUrl = await this.photoService.downloadScoutRefPhotoFile(
            imageDoc._id,
            imageDoc.mimeType
          );
        }
      }
      if (dataUrl) {
        this.overlayImageCache.set(imageId, dataUrl);
        return dataUrl;
      }
    } catch (error) {
      console.error(`resolveOverlayImage(${imageId}) failed`, error);
    }
    return undefined;
  }

  private computeOverlayBounds(overlay: MapImageOverlay) {
    if (!overlay) {
      return null;
    }
    const fromBounds = overlay.bounds;
    const parseLatLon = (pt?: { x?: number; y?: number }) => {
      if (pt == null) {
        return null;
      }
      const lat = Number(pt.x);
      const lon = Number(pt.y);
      if (!isFinite(lat) || !isFinite(lon)) {
        return null;
      }
      return { lat, lon };
    };
    const topLeft = parseLatLon(fromBounds?.topLeft);
    const bottomRight = parseLatLon(fromBounds?.bottomRight);
    if (topLeft && bottomRight) {
      return {
        west: Math.min(topLeft.lon, bottomRight.lon),
        south: Math.min(topLeft.lat, bottomRight.lat),
        east: Math.max(topLeft.lon, bottomRight.lon),
        north: Math.max(topLeft.lat, bottomRight.lat),
      };
    }

    if (
      overlay.topLeft &&
      overlay.pixelSize &&
      overlay.imageSize &&
      isFinite(Number(overlay.pixelSize.width)) &&
      isFinite(Number(overlay.pixelSize.height)) &&
      isFinite(Number(overlay.imageSize.width)) &&
      isFinite(Number(overlay.imageSize.height))
    ) {
      const tlX = Number(overlay.topLeft.x);
      const tlY = Number(overlay.topLeft.y);
      const pxWidth = Number(overlay.pixelSize.width);
      const pxHeight = Number(overlay.pixelSize.height);
      const imgWidth = Number(overlay.imageSize.width);
      const imgHeight = Number(overlay.imageSize.height);
      if ([tlX, tlY].every((v) => isFinite(v))) {
        const brX = tlX + pxWidth * imgWidth;
        const brY = tlY + pxHeight * imgHeight;
        const tlLatLon = this.webMercatorToDegrees(tlX, tlY);
        const brLatLon = this.webMercatorToDegrees(brX, brY);
        if (tlLatLon && brLatLon) {
          return {
            west: Math.min(tlLatLon.lon, brLatLon.lon),
            south: Math.min(tlLatLon.lat, brLatLon.lat),
            east: Math.max(tlLatLon.lon, brLatLon.lon),
            north: Math.max(tlLatLon.lat, brLatLon.lat),
          };
        }
      }
    }

    return null;
  }

  private webMercatorToDegrees(x: number, y: number) {
    if (!isFinite(x) || !isFinite(y)) {
      return null;
    }
    const RADIUS = 6378137.0;
    const lon = (x / RADIUS) * (180.0 / Math.PI);
    const lat =
      (2 * Math.atan(Math.exp(y / RADIUS)) - Math.PI / 2) * (180.0 / Math.PI);
    const clampedLat = Math.max(Math.min(lat, 90), -90);
    return { lat: clampedLat, lon };
  }

  private getOverlayAltitude(path: KmlBuilderPath, overlay: MapImageOverlay) {
    const candidateValues = [
      overlay.altitudeEllipsoidMeters,
      overlay.altitudeMeters,
      overlay.altitude,
    ];
    let altitude = candidateValues
      .map((value) => {
        if (value === undefined || value === null) {
          return undefined;
        }
        const numericValue = Number(value);
        return Number.isFinite(numericValue) ? numericValue : undefined;
      })
      .find((value) => value != null);
    const derivedFromOverlay = altitude != null;
    if (altitude == null) {
      altitude = this.findPrimaryHaAltitude(path);
    }
    if (altitude == null || !isFinite(altitude)) {
      return undefined;
    }
    if (!derivedFromOverlay) {
      const adjusted = altitude - this.surfaceOffsetMeters;
      return adjusted > 0 ? adjusted : Math.max(altitude, 0);
    }
    return altitude;
  }

  private findPrimaryHaAltitude(path: KmlBuilderPath) {
    const points = path?.points || [];
    if (!points.length) {
      return undefined;
    }
    const primaryHa = points.find((pt) => {
      const type = (pt?.type || '').toUpperCase();
      const isRoot = !pt?.parentId;
      return type === 'HA' && isRoot;
    });
    const candidatePoints = primaryHa ? [primaryHa, ...points] : points;
    for (const pt of candidatePoints) {
      const altitude = pt?.altitudeEllipsoidMeters;
      if (altitude != null && isFinite(Number(altitude))) {
        return Number(altitude);
      }
    }
    return undefined;
  }

  // Build a stacked, virtual building using per-path bounds and HA altitudes
  async createVirtualBuildingFromPaths(paths: KmlBuilderPath[], viewer: Viewer) {
    try {
      console.log('🏗️ Building virtual stacked boxes from path points');

      // Collect per-path stats
      type PathStats = {
        path: KmlBuilderPath;
        floorAlt: number | null; // meters (ellipsoid)
        bounds: { west: number; south: number; east: number; north: number } | null;
        centroid?: { lon: number; lat: number } | null;
      };

      const pathStats: PathStats[] = [];
      let globalWest = +Infinity, globalSouth = +Infinity, globalEast = -Infinity, globalNorth = -Infinity;

      for (const path of paths) {
        const pts = (path.points || []).filter(p => p && p.longitude != null && p.latitude != null);
        if (!pts.length) {
          pathStats.push({ path, floorAlt: null, bounds: null, centroid: null });
          continue;
        }

        // Bounds for this path
        let west = +Infinity, south = +Infinity, east = -Infinity, north = -Infinity;
        let sumLon = 0, sumLat = 0;
        for (const p of pts) {
          const lon = Number(p.longitude), lat = Number(p.latitude);
          if (lon < west) west = lon; if (lon > east) east = lon;
          if (lat < south) south = lat; if (lat > north) north = lat;
          sumLon += lon; sumLat += lat;
        }
        globalWest = Math.min(globalWest, west);
        globalSouth = Math.min(globalSouth, south);
        globalEast = Math.max(globalEast, east);
        globalNorth = Math.max(globalNorth, north);

        // Floor altitude from HA points (median); fallback to median of all point alts
        const haPts = pts.filter(p => p.type === 'HA' && !p.parentId && p.altitudeEllipsoidMeters != null);
        const alts = (haPts.length ? haPts : pts)
          .map(p => Number(p.altitudeEllipsoidMeters))
          .filter(a => isFinite(a));
        alts.sort((a,b)=>a-b);
        const medianAlt = alts.length ? alts[Math.floor(alts.length/2)] : null;

        pathStats.push({
          path,
          floorAlt: medianAlt,
          bounds: { west, south, east, north },
          centroid: { lon: sumLon/pts.length, lat: sumLat/pts.length }
        });
      }

      if (!isFinite(globalWest) || !isFinite(globalSouth) || !isFinite(globalEast) || !isFinite(globalNorth)) {
        console.warn('⚠️ No valid points to compute global bounds');
        return;
      }

      // Determine ground height at global centroid
      let groundHeight = 0;
      try {
        const cx = (globalWest + globalEast) / 2;
        const cy = (globalSouth + globalNorth) / 2;
        const terrainProvider = viewer.terrainProvider;
        if (terrainProvider && (terrainProvider as any).availability) {
          const terrainHeight = await Cesium.sampleTerrainMostDetailed(
            terrainProvider,
            [Cesium.Cartographic.fromDegrees(cx, cy)]
          );
          groundHeight = terrainHeight[0]?.height || 0;
        }
      } catch (e) {
        console.warn('⚠️ Could not sample ground; using 0');
      }

      // Sort floors by altitude, associate each with its bounds
      const floors = pathStats
        .filter(ps => ps.floorAlt != null && ps.bounds != null)
        .sort((a,b) => (a.floorAlt! - b.floorAlt!));

      if (!floors.length) {
        console.warn('⚠️ No floors with altitude; skipping virtual building');
        return;
      }

      // Derive vertical spans using mid-planes between adjacent floors
      const defaultThickness = 4; // meters
      const toSpan = (idx: number): { bottom: number; top: number } => {
        const curr = floors[idx].floorAlt!;
        if (idx === 0) {
          const next = floors[1]?.floorAlt;
          const midUp = next != null ? (curr + next) / 2 : (curr + defaultThickness);
          return { bottom: groundHeight, top: midUp };
        } else if (idx === floors.length - 1) {
          const prev = floors[idx - 1].floorAlt!;
          const midDown = (prev + curr) / 2;
          const top = curr + defaultThickness;
          return { bottom: midDown, top };
        } else {
          const prev = floors[idx - 1].floorAlt!;
          const next = floors[idx + 1].floorAlt!;
          return { bottom: (prev + curr) / 2, top: (curr + next) / 2 };
        }
      };

      // Colors: fade as we go up
      const baseColor = Cesium.Color.CYAN.withAlpha(0.10);
      const topColor = Cesium.Color.ORANGE.withAlpha(0.18);
      const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
      const lerpColor = (t: number) => new Cesium.Color(
        lerp(baseColor.red, topColor.red, t),
        lerp(baseColor.green, topColor.green, t),
        lerp(baseColor.blue, topColor.blue, t),
        lerp(baseColor.alpha, topColor.alpha, t)
      );

      // Draw bottom-most box using global footprint from all points
      const globalSpan = toSpan(0);
      const bottomColor = lerpColor(0);
      console.log('📦 Adding bottom box', { globalWest, globalSouth, globalEast, globalNorth, globalSpan });
      viewer.entities.add({
        name: 'Virtual Building - Base',
        polygon: {
          hierarchy: Cesium.Cartesian3.fromDegreesArray([
            globalWest, globalSouth,
            globalEast, globalSouth,
            globalEast, globalNorth,
            globalWest, globalNorth
          ]),
          height: globalSpan.bottom,
          extrudedHeight: globalSpan.top,
          material: bottomColor,
          outline: true,
          outlineColor: Cesium.Color.CYAN,
          heightReference: Cesium.HeightReference.NONE
        }
      });

      // Draw higher boxes using per-path bounds
      for (let i = 1; i < floors.length; i++) {
        const ps = floors[i];
        const span = toSpan(i);
        const b = ps.bounds!;
        const color = lerpColor(i / Math.max(1, floors.length - 1));
        console.log(`📦 Adding level ${i + 1}/${floors.length}`, { bounds: b, span });
        viewer.entities.add({
          name: `Virtual Building - Level ${i + 1}`,
          polygon: {
            hierarchy: Cesium.Cartesian3.fromDegreesArray([
              b.west, b.south,
              b.east, b.south,
              b.east, b.north,
              b.west, b.north
            ]),
            height: span.bottom,
            extrudedHeight: span.top,
            material: color,
            outline: true,
            outlineColor: Cesium.Color.ORANGE,
            heightReference: Cesium.HeightReference.NONE
          }
        });
      }

      console.log('🏁 Virtual building completed');
    } catch (error) {
      console.error('❌ Error creating virtual building from paths:', error);
    }
  }

  // Build a stacked, virtual building of cylinders (ellipses) using HA levels
  async createVirtualBuildingCylindersFromPaths(paths: KmlBuilderPath[], viewer: Viewer) {
    try {
      console.log('🏗️ Building per-path bounding discs (independent)');

      // Helper to compute simple centroid of lon/lat points
      const centroid = (pts: any[]) => {
        let sx = 0, sy = 0;
        for (const p of pts) { sx += Number(p.longitude); sy += Number(p.latitude); }
        return { lon: sx / pts.length, lat: sy / pts.length };
      };

      const color = Cesium.Color.fromAlpha(Cesium.Color.CYAN, 0.08);
      const groundPadMeters = 0.5; // ensure surfaces aren't underground

      for (let idx = 0; idx < paths.length; idx++) {
        const path = paths[idx];
        const pts = (path.points || []).filter(p => p && p.longitude != null && p.latitude != null);
        if (!pts.length) continue;

        const { lon, lat } = centroid(pts);
        const centerCart = Cesium.Cartesian3.fromDegrees(lon, lat, 0);

        // Compute radius: max distance from centroid to any point (meters)
        let radius = 0;
        for (const p of pts) {
          const c = Cesium.Cartesian3.fromDegrees(Number(p.longitude), Number(p.latitude), 0);
          const d = Cesium.Cartesian3.distance(centerCart, c);
          if (isFinite(d)) radius = Math.max(radius, d);
        }
        radius = Math.max(2, radius + 1.0);

        // Altitude: prefer HA median, else all points median
        const haPoints = pts.filter(p => p.type === 'HA' && !p.parentId && p.altitudeEllipsoidMeters != null);
        const alts = (haPoints.length ? haPoints : pts)
          .map(p => Number(p.altitudeEllipsoidMeters))
          .filter(a => isFinite(a));
        if (!alts.length) continue;
        alts.sort((a,b)=>a-b);
        let alt = alts[Math.floor(alts.length/2)];

        // Clamp to ground if needed so discs don't sit below terrain
        try {
          const terrainProvider = viewer.terrainProvider;
          if (terrainProvider && (terrainProvider as any).availability) {
            const t = await Cesium.sampleTerrainMostDetailed(
              terrainProvider,
              [Cesium.Cartographic.fromDegrees(lon, lat)]
            );
            const ground = t[0]?.height;
            if (isFinite(ground)) {
              alt = Math.max(alt, ground + groundPadMeters);
            }
          }
        } catch (e) {
          // non-fatal
        }

        console.log('🟠 Adding independent path disc', { pathId: path.id, lon, lat, radius, alt });
        const discEntity = viewer.entities.add({
          name: `Path Disc ${idx+1}`,
          position: Cesium.Cartesian3.fromDegrees(lon, lat, 0),
          ellipse: {
            semiMajorAxis: radius,
            semiMinorAxis: radius,
            height: alt - this.surfaceOffsetMeters,
            material: color,
            outline: true,
            outlineColor: Cesium.Color.fromAlpha(Cesium.Color.WHITE, 0.3),
            heightReference: Cesium.HeightReference.NONE
          }
        });
        this.additionalEntities.push(discEntity);

        // Add vertical line from HA point to ground for each HA point in this path
        for (const haPoint of haPoints) {
          const verticalEntities = await this.addVerticalLineToGround(haPoint, viewer);
          this.additionalEntities.push(...verticalEntities);
        }

        // Optional label per disc
        const labelEntity = viewer.entities.add({
          position: Cesium.Cartesian3.fromDegrees(lon, lat, alt + 2),
          label: {
            text: `${path.buildingId||''} P${(path.pathNumber||'').toString().padStart(2,'0')} @ ${alt.toFixed(1)}m`,
            font: '10pt sans-serif',
            style: Cesium.LabelStyle.FILL_AND_OUTLINE,
            outlineWidth: 2,
            verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
            fillColor: Cesium.Color.WHITE,
            outlineColor: Cesium.Color.BLACK,
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
            heightReference: Cesium.HeightReference.NONE
          }
        });
        this.additionalEntities.push(labelEntity);

        // Add vertical dotted lines from misc leaf children (e.g., call logs) to this disc altitude
        try {
          const isMiscChild = (p: any) => {
            const t = (p?.type || '').toLowerCase();
            const emptyPointId = !p?.pointId; // extras used empty pointId in KML builder
            return emptyPointId || t.includes('call');
          };
          const discAlt = alt - this.surfaceOffsetMeters;
          for (const p of pts) {
            if (!isMiscChild(p)) continue;
            const plon = Number(p.longitude), plat = Number(p.latitude);
            const pAlt = Number(p.altitudeEllipsoidMeters);
            if (!isFinite(plon) || !isFinite(plat)) continue;
            const startAlt = isFinite(pAlt) ? pAlt : discAlt;
            const below = isFinite(pAlt) ? (pAlt < discAlt) : false;
            const positions = Cesium.Cartesian3.fromDegreesArrayHeights([
              plon, plat, startAlt,
              plon, plat, discAlt
            ]);
            const connectorColor = below ? Cesium.Color.RED.withAlpha(0.8) : Cesium.Color.WHITE.withAlpha(0.65);
            const lineEntity = viewer.entities.add({
              name: `Misc Child Connector`,
              polyline: {
                positions,
                width: 1.4,
                material: new Cesium.PolylineDashMaterialProperty({
                  color: connectorColor,
                  dashLength: 8
                }),
                clampToGround: false,
                arcType: Cesium.ArcType.NONE,
                depthFailMaterial: new Cesium.ColorMaterialProperty(connectorColor.withAlpha(0.35))
              }
            });
            this.additionalEntities.push(lineEntity);

            // Dot at the plane intersection
            const dotEntity = viewer.entities.add({
              name: `Misc Child Plane Dot`,
              position: Cesium.Cartesian3.fromDegrees(plon, plat, discAlt),
              point: {
                pixelSize: 6,
                color: Cesium.Color.fromAlpha(Cesium.Color.CYAN, 0.85),
                outlineColor: Cesium.Color.BLACK,
                outlineWidth: 1,
                heightReference: Cesium.HeightReference.NONE,
                disableDepthTestDistance: Number.POSITIVE_INFINITY
              }
            });
            this.additionalEntities.push(dotEntity);

            // Short horizontal (in-plane) dotted segment centered at the intersection
            try {
              const base = Cesium.Cartesian3.fromDegrees(plon, plat, discAlt);
              const enu = Cesium.Transforms.eastNorthUpToFixedFrame(base);
              const half = 1.5; // meters
              const pA = Cesium.Matrix4.multiplyByPoint(enu, new Cesium.Cartesian3(+half, 0, 0), new Cesium.Cartesian3());
              const pB = Cesium.Matrix4.multiplyByPoint(enu, new Cesium.Cartesian3(-half, 0, 0), new Cesium.Cartesian3());
              const horizEntity = viewer.entities.add({
                name: `Misc Child Plane Horizontal`,
                polyline: {
                  positions: [pA, pB],
                  width: 1.6,
                  material: new Cesium.PolylineDashMaterialProperty({
                    color: Cesium.Color.fromAlpha(Cesium.Color.CYAN, 0.7),
                    dashLength: 6
                  }),
                  clampToGround: false,
                  arcType: Cesium.ArcType.NONE,
                  depthFailMaterial: new Cesium.ColorMaterialProperty(Cesium.Color.fromAlpha(Cesium.Color.CYAN, 0.35))
                }
              });
              this.additionalEntities.push(horizEntity);
            } catch {}
          }
        } catch (e) {
          console.warn('Failed adding misc child connector lines', e);
        }
      }

      console.log('🏁 Virtual cylinders building completed');
    } catch (error) {
      console.error('❌ Error creating virtual cylinders from paths:', error);
    }
  }

  async createFloorPolygonsFromHAPoints(paths: KmlBuilderPath[], viewer: Viewer) {
    try {
      console.log('🏗️ Starting floor polygon creation for HA points');
      console.log(`📊 Processing ${paths.length} paths`);
      
      // Wait for building tiles and terrain to be ready
      console.log('⏳ Waiting for building tiles and terrain...');
      await this.waitForBuildingsAndTerrain();
      console.log('✅ Building tiles and terrain ready');
      
      let totalHAPoints = 0;
      let processedHAPoints = 0;
      
      for (const path of paths) {
        // Find HA points in this path
        const haPoints = path.points.filter(pt => pt.type === 'HA' && !pt.parentId);
        totalHAPoints += haPoints.length;
        
        console.log(`📍 Path ${path.id}: Found ${haPoints.length} HA points`);
        
        for (const haPoint of haPoints) {
          console.log(`🎯 Processing HA point: ${haPoint.pointId || 'unnamed'} at (${haPoint.longitude}, ${haPoint.latitude}, ${haPoint.altitudeEllipsoidMeters})`);
          await this.createFloorForHAPoint(haPoint, viewer);
          processedHAPoints++;
          console.log(`✅ Floor created for HA point ${processedHAPoints}/${totalHAPoints}`);
        }
      }
      
      console.log(`🏁 Completed floor polygon creation: ${processedHAPoints}/${totalHAPoints} HA points processed`);
    } catch (error) {
      console.error('❌ Error creating floor polygons from HA points:', error);
    }
  }

  async waitForBuildingsAndTerrain() {
    // Wait for OSM building tiles to load if they exist
    if (this.osmBuildingsTileset) {
      console.log('🏢 OSM building tileset exists, checking readiness...');
      return new Promise<void>((resolve) => {
        if ((this.osmBuildingsTileset as any).ready) {
          console.log('✅ OSM building tileset already ready');
          resolve();
        } else {
          console.log('⏳ OSM building tileset not ready, waiting...');
          const readyPromise = (this.osmBuildingsTileset as any).readyPromise;
          if (readyPromise && readyPromise.then) {
            readyPromise.then(() => {
              console.log('✅ OSM building tileset ready via promise');
              resolve();
            });
          } else {
            console.log('⚠️ No readyPromise available, using fallback timeout');
            // Fallback - wait a short time for tiles to load
            setTimeout(() => {
              console.log('⏰ Fallback timeout completed');
              resolve();
            }, 1000);
          }
        }
      });
    } else {
      console.log('❌ No OSM building tileset available');
    }
  }

  async createFloorForHAPoint(haPoint: any, viewer: Viewer) {
    try {
      console.log(`🔍 Creating floor for HA point: ${haPoint.pointId}`);
      let groundHeight = 0;
      
      // Sample terrain height at HA point location
      console.log(`🌍 Sampling terrain at (${haPoint.longitude}, ${haPoint.latitude})`);
      try {
        const terrainProvider = viewer.terrainProvider;
        console.log(`🗺️ Terrain provider:`, terrainProvider?.constructor?.name || 'Unknown');
        
        if (terrainProvider && terrainProvider.availability) {
          console.log(`✅ Terrain provider available, sampling height...`);
          const terrainHeight = await Cesium.sampleTerrainMostDetailed(
            terrainProvider,
            [Cesium.Cartographic.fromDegrees(haPoint.longitude, haPoint.latitude)]
          );
          groundHeight = terrainHeight[0]?.height || 0;
          console.log(`📏 Ground height sampled: ${groundHeight}m`);
        } else {
          console.log(`❌ Terrain provider not available or no availability data`);
        }
      } catch (terrainError) {
        console.warn('⚠️ Could not sample terrain height, using ground level 0:', terrainError);
      }
      
      const floorHeight = haPoint.altitudeEllipsoidMeters || groundHeight;
      console.log(`🏗️ Floor height: ${floorHeight}m (HA: ${haPoint.altitudeEllipsoidMeters}, Ground: ${groundHeight})`);
      
      // Try to detect building footprint from OSM tiles
      console.log(`🏢 Attempting to detect building footprint...`);
      const buildingFootprint = await this.detectBuildingFootprint(haPoint, viewer);
      
      if (buildingFootprint.length > 0) {
        console.log(`🎯 Building footprint detected! Creating orange floor polygon with ${buildingFootprint.length} points`);
        // Create floor polygon using detected building footprint
        const floorEntity = viewer.entities.add({
          name: `Building Floor at ${haPoint.pointId || 'HA'}`,
          polygon: {
            hierarchy: buildingFootprint,
            height: floorHeight,
            material: Cesium.Color.ORANGE.withAlpha(0.4),
            outline: true,
            outlineColor: Cesium.Color.ORANGE,
            heightReference: Cesium.HeightReference.NONE
          }
        });
        
        if (floorEntity.polygon) {
          (floorEntity.polygon as any).disableDepthTestDistance = Number.POSITIVE_INFINITY;
        }
      } else {
        console.log(`❌ No building footprint detected, creating fallback cyan rectangular floor`);
        // Fallback to rectangular floor if no building detected
        const offsetDegrees = 0.0001; // ~10 meters
        const floorEntity = viewer.entities.add({
          name: `Floor at ${haPoint.pointId || 'HA'}`,
          polygon: {
            hierarchy: Cesium.Cartesian3.fromDegreesArray([
              haPoint.longitude - offsetDegrees, haPoint.latitude - offsetDegrees,
              haPoint.longitude + offsetDegrees, haPoint.latitude - offsetDegrees,
              haPoint.longitude + offsetDegrees, haPoint.latitude + offsetDegrees,
              haPoint.longitude - offsetDegrees, haPoint.latitude + offsetDegrees
            ]),
            height: floorHeight,
            material: Cesium.Color.CYAN.withAlpha(0.3),
            outline: true,
            outlineColor: Cesium.Color.CYAN,
            heightReference: Cesium.HeightReference.NONE
          }
        });
        
        if (floorEntity.polygon) {
          (floorEntity.polygon as any).disableDepthTestDistance = Number.POSITIVE_INFINITY;
        }
      }
      
      // Add floor label
      console.log(`🏷️ Adding floor label at height ${floorHeight + 2}m`);
      viewer.entities.add({
        name: `Floor Label ${haPoint.pointId || 'HA'}`,
        position: Cesium.Cartesian3.fromDegrees(
          haPoint.longitude, 
          haPoint.latitude, 
          floorHeight + 2
        ),
        label: {
          text: `${haPoint.pointId || 'HA'}: ${floorHeight.toFixed(1)}m (${(floorHeight - groundHeight).toFixed(1)}m above ground)`,
          font: '11pt sans-serif',
          style: Cesium.LabelStyle.FILL_AND_OUTLINE,
          outlineWidth: 2,
          verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
          pixelOffset: new Cesium.Cartesian2(0, -30),
          fillColor: Cesium.Color.WHITE,
          outlineColor: Cesium.Color.BLACK,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          heightReference: Cesium.HeightReference.NONE
        }
      });
      
      console.log(`✅ Floor creation completed for HA point: ${haPoint.pointId}`);
    } catch (error) {
      console.error(`❌ Error creating floor for HA point ${haPoint.pointId}:`, error);
    }
  }

  async detectBuildingFootprint(haPoint: any, viewer: Viewer): Promise<Cartesian3[]> {
    // Helper: convert world position to window coordinates
    const worldToWindow = (pos: any) => {
      try {
        return Cesium.SceneTransforms.worldToWindowCoordinates(viewer.scene, pos);
      } catch {
        try {
          return (viewer.scene as any).cartesianToCanvasCoordinates(pos);
        } catch {
          return undefined;
        }
      }
    };

    // Helper: pick OSM building feature at a window position
    const pickOsmBuildingFeature = (winPos: any) => {
      // Prefer drillPick to avoid KML entity hits masking tiles
      const results: any[] = viewer.scene.drillPick(winPos, 8) || [];
      for (const r of results) {
        const f = r?.id || r; // Cesium sometimes stores the feature in id
        const tileset = (r as any).tileset || (r as any).primitive?.tileset || (f as any)?.tileset || undefined;
        const isOsm = tileset === this.osmBuildingsTileset;
        const is3DTileFeature = typeof (r as any).getProperty === 'function' || typeof (f as any)?.getProperty === 'function';
        if (isOsm || is3DTileFeature) {
          return r;
        }
      }
      return undefined;
    };

    // Helper: compare features (best-effort)
    const sameFeature = (a: any, b: any) => {
      if (!a || !b) return false;
      // Prefer strict reference compare
      if (a === b) return true;
      // Compare unique-ish properties if available
      const aId = (a as any)?.featureId ?? (a as any)?.batchId ?? (a as any)?._batchId ?? (a as any)?.id;
      const bId = (b as any)?.featureId ?? (b as any)?.batchId ?? (b as any)?._batchId ?? (b as any)?.id;
      if (aId != null && bId != null) return aId === bId;
      return false;
    };

    // Helper: screen point to ground Cartographic
    const screenToCartographic = (winPos: any) => {
      const ray = viewer.camera.getPickRay(winPos);
      const cartesian = viewer.scene.globe.pick(ray, viewer.scene);
      if (!cartesian) return undefined;
      return Cesium.Ellipsoid.WGS84.cartesianToCartographic(cartesian);
    };

    try {
      console.log(`🔍 Detecting building footprint for HA point at (${haPoint.longitude}, ${haPoint.latitude})`);
      if (!this.osmBuildingsTileset) {
        console.log(`❌ No OSM building tileset available for footprint detection`);
        return [];
      }

      // Convert HA geodetic to window coordinates
      const world = Cesium.Cartesian3.fromDegrees(haPoint.longitude, haPoint.latitude, haPoint.altitudeEllipsoidMeters || 0);
      const centerWin = worldToWindow(world);
      if (!centerWin) {
        console.log(`❌ Could not convert HA point to window coordinates`);
        return [];
      }

      // Identify the building feature at the HA location (or very near it)
      const firstPick = pickOsmBuildingFeature(centerWin);
      if (!firstPick) {
        // Try a small cross search in case the point is slightly offset
        const offsets = [
          [0, 0], [4, 0], [-4, 0], [0, 4], [0, -4],
        ];
        for (const [dx, dy] of offsets) {
          const p = pickOsmBuildingFeature(new Cesium.Cartesian2(centerWin.x + dx, centerWin.y + dy));
          if (p) {
            console.log('✅ Found nearby building feature on offset search');
            (centerWin as any).x += dx; (centerWin as any).y += dy;
            break;
          }
        }
      }

      const buildingFeature = firstPick || pickOsmBuildingFeature(centerWin);
      if (!buildingFeature) {
        console.log('❌ No building feature found at/near HA point');
        return [];
      }

      // Radial sampling to approximate the footprint outline in screen-space
      const maxRadiusPx = 80; // search up to ~80px from center
      const stepPx = 4;       // radial sampling step
      const angleStepDeg = 20; // 18 points ~ circle
      const boundaryCartographics: any[] = [];

      for (let angle = 0; angle < 360; angle += angleStepDeg) {
        const theta = Cesium.Math.toRadians(angle);
        let lastInsideWinPos: any = undefined;
        let foundEdge = false;
        for (let r = 0; r <= maxRadiusPx; r += stepPx) {
          const testWin = new Cesium.Cartesian2(centerWin.x + Math.cos(theta) * r, centerWin.y + Math.sin(theta) * r);
          const picked = pickOsmBuildingFeature(testWin);
          if (!picked || !sameFeature(picked, buildingFeature)) {
            // We just crossed the edge; use the last inside point
            if (lastInsideWinPos) {
              const carto = screenToCartographic(lastInsideWinPos);
              if (carto) {
                boundaryCartographics.push(carto);
              }
            }
            foundEdge = true;
            break;
          } else {
            lastInsideWinPos = testWin;
          }
        }
        if (!foundEdge && lastInsideWinPos) {
          // If we never left the building within max radius, still record last point
          const carto = screenToCartographic(lastInsideWinPos);
          if (carto) boundaryCartographics.push(carto);
        }
      }

      if (boundaryCartographics.length < 3) {
        console.log('⚠️ Insufficient boundary points; using small rectangle fallback');
        const offsetDegrees = 0.00015; // ~15m
        return Cesium.Cartesian3.fromDegreesArray([
          haPoint.longitude - offsetDegrees, haPoint.latitude - offsetDegrees,
          haPoint.longitude + offsetDegrees, haPoint.latitude - offsetDegrees,
          haPoint.longitude + offsetDegrees, haPoint.latitude + offsetDegrees,
          haPoint.longitude - offsetDegrees, haPoint.latitude + offsetDegrees,
        ]);
      }

      // Convert boundary points to degrees and build polygon positions (close the ring)
      const degs: number[] = [];
      for (const c of boundaryCartographics) {
        degs.push(Cesium.Math.toDegrees(c.longitude), Cesium.Math.toDegrees(c.latitude));
      }
      // Ensure ring closure by appending first point again if needed
      if (degs.length >= 2) {
        degs.push(degs[0], degs[1]);
      }

      const positions = Cesium.Cartesian3.fromDegreesArray(degs);
      console.log(`✅ Approximated footprint with ${positions.length} positions`);
      return positions;
    } catch (error) {
      console.warn('⚠️ Could not detect building footprint:', error);
      return [];
    }
  }

  async addVerticalLineToGround(haPoint: any, viewer: Viewer): Promise<any[]> {
    const entities: any[] = [];
    try {
      console.log(`📏 Adding vertical line for HA point: ${haPoint.pointId} at (${haPoint.longitude}, ${haPoint.latitude})`);
      
      // Sample terrain height at HA point location
      let groundHeight = 0;
      try {
        const terrainProvider = viewer.terrainProvider;
        if (terrainProvider && (terrainProvider as any).availability) {
          const terrainHeight = await Cesium.sampleTerrainMostDetailed(
            terrainProvider,
            [Cesium.Cartographic.fromDegrees(haPoint.longitude, haPoint.latitude)]
          );
          groundHeight = terrainHeight[0]?.height || 0;
          console.log(`🌍 Ground height at HA point: ${groundHeight.toFixed(2)}m`);
        }
      } catch (terrainError) {
        console.warn('⚠️ Could not sample terrain height for vertical line:', terrainError);
      }
      
      const haHeight = haPoint.altitudeEllipsoidMeters || groundHeight;
      const heightDifference = haHeight - groundHeight;
      
      console.log(`📐 HA height: ${haHeight.toFixed(2)}m, Ground: ${groundHeight.toFixed(2)}m, Difference: ${heightDifference.toFixed(2)}m`);
      
      // Choose color: gray for above ground, red for below ground
      const lineColor = heightDifference >= 0 
        ? Cesium.Color.GRAY.withAlpha(0.4)      // Gray if above ground
        : Cesium.Color.RED.withAlpha(0.4);      // Red if below ground
      
      const lineWidth = 2;
      
      // Create dotted/dashed material
      const dashedMaterial = new Cesium.PolylineDashMaterialProperty({
        color: lineColor,
        dashLength: 8.0,
        dashPattern: parseInt('11110000', 2) // Dotted pattern
      });

      // Create vertical line from HA point to ground
      const lineEntity = viewer.entities.add({
        name: `Vertical Line - ${haPoint.pointId || 'HA'}`,
        polyline: {
          positions: [
            Cesium.Cartesian3.fromDegrees(haPoint.longitude, haPoint.latitude, Math.min(haHeight, groundHeight)),
            Cesium.Cartesian3.fromDegrees(haPoint.longitude, haPoint.latitude, Math.max(haHeight, groundHeight))
          ],
          width: lineWidth,
          material: dashedMaterial,
          clampToGround: false,
          arcType: Cesium.ArcType.NONE
        }
      });
      entities.push(lineEntity);
      
      // Set properties after creation (if supported)
      if (lineEntity.polyline) {
        try {
          (lineEntity.polyline as any).heightReference = Cesium.HeightReference.NONE;
          (lineEntity.polyline as any).disableDepthTestDistance = Number.POSITIVE_INFINITY;
        } catch (e) {
          // These properties may not be available on polylines
        }
      }
      
      // Add a small marker at ground level
      const groundMarker = viewer.entities.add({
        name: `Ground Marker - ${haPoint.pointId || 'HA'}`,
        position: Cesium.Cartesian3.fromDegrees(haPoint.longitude, haPoint.latitude, groundHeight),
        point: {
          pixelSize: 8,
          color: Cesium.Color.YELLOW.withAlpha(0.9),
          outlineColor: Cesium.Color.BLACK,
          outlineWidth: 1,
          heightReference: Cesium.HeightReference.NONE,
          disableDepthTestDistance: Number.POSITIVE_INFINITY
        }
      });
      entities.push(groundMarker);
      
      // Add elevation difference label
      const midHeight = (haHeight + groundHeight) / 2;
      const elevationLabel = viewer.entities.add({
        name: `Elevation Difference - ${haPoint.pointId || 'HA'}`,
        position: Cesium.Cartesian3.fromDegrees(haPoint.longitude, haPoint.latitude, midHeight),
        label: {
          text: `${heightDifference >= 0 ? '+' : ''}${heightDifference.toFixed(1)}m`,
          font: '11pt monospace',
          style: Cesium.LabelStyle.FILL_AND_OUTLINE,
          outlineWidth: 2,
          verticalOrigin: Cesium.VerticalOrigin.CENTER,
          horizontalOrigin: Cesium.HorizontalOrigin.LEFT,
          pixelOffset: new Cesium.Cartesian2(15, 0), // Offset to avoid overlapping the line
          fillColor: heightDifference >= 0 ? Cesium.Color.GRAY : Cesium.Color.RED,
          outlineColor: Cesium.Color.BLACK,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          heightReference: Cesium.HeightReference.NONE
        }
      });
      entities.push(elevationLabel);
      
      console.log(`✅ Vertical line added for HA point: ${haPoint.pointId}`);
    } catch (error) {
      console.error(`❌ Error adding vertical line for HA point ${haPoint.pointId}:`, error);
    }
    return entities;
  }

  escapeXMLSpecialChars(unsafe: string) {
    return unsafe
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }
}
